import faker from 'faker';
import {
	AlertOctagon,
	Calendar,
	CheckCircle2,
	Clock,
	Users,
	XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Toaster } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

faker.seed(33);

type UserRole = 'Analyst' | 'Supervisor' | 'Admin';
type UserStatus = 'Active' | 'Inactive';
type ModuleHealth = 'Healthy' | 'Warning' | 'Error';

type AmlAdminUser = {
	id: string;
	name: string;
	role: UserRole;
	activeCases: number;
	activeAlerts: number;
	lastLogin: string;
	status: UserStatus;
	email: string;
	assignedSince: string;
};

type SystemModule = {
	id: string;
	module: string;
	status: ModuleHealth;
	lastRun: string;
	recordsProcessed: number;
	errorCount: number;
};

const ROLES: UserRole[] = ['Analyst', 'Supervisor', 'Admin'];
const STATUSES: UserStatus[] = ['Active', 'Active', 'Active', 'Inactive'];

const MOCK_USERS: AmlAdminUser[] = Array.from({ length: 8 }, () => ({
	id: faker.datatype.uuid(),
	name: faker.name.findName(),
	role: ROLES[faker.datatype.number({ min: 0, max: ROLES.length - 1 })],
	activeCases: faker.datatype.number({ min: 0, max: 12 }),
	activeAlerts: faker.datatype.number({ min: 0, max: 30 }),
	lastLogin: faker.date.recent(7).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	}),
	status: STATUSES[faker.datatype.number({ min: 0, max: STATUSES.length - 1 })],
	email: faker.internet.email(),
	assignedSince: faker.date.past(2).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	}),
}));

const SYSTEM_MODULES: SystemModule[] = [
	{
		id: 'mod_ns',
		module: 'Name Screening Engine',
		status: 'Healthy',
		lastRun: 'May 26, 2025 14:02 UTC',
		recordsProcessed: 48203,
		errorCount: 0,
	},
	{
		id: 'mod_wl',
		module: 'Watchlist Sync',
		status: 'Healthy',
		lastRun: 'May 26, 2025 06:00 UTC',
		recordsProcessed: 12450,
		errorCount: 0,
	},
	{
		id: 'mod_bs',
		module: 'Batch Screening',
		status: 'Warning',
		lastRun: 'May 26, 2025 02:00 UTC',
		recordsProcessed: 22100,
		errorCount: 3,
	},
	{
		id: 'mod_str',
		module: 'STR Processor',
		status: 'Healthy',
		lastRun: 'May 25, 2025 18:30 UTC',
		recordsProcessed: 17,
		errorCount: 0,
	},
	{
		id: 'mod_ca',
		module: 'Case Automation',
		status: 'Error',
		lastRun: 'May 25, 2025 12:00 UTC',
		recordsProcessed: 0,
		errorCount: 14,
	},
];

function roleBadge(role: UserRole) {
	switch (role) {
		case 'Admin':
			return (
				<Badge className="border border-purple-200 bg-purple-50 text-purple-700 text-xs hover:bg-purple-50">
					Admin
				</Badge>
			);
		case 'Supervisor':
			return (
				<Badge className="border border-blue-200 bg-blue-50 text-blue-700 text-xs hover:bg-blue-50">
					Supervisor
				</Badge>
			);
		case 'Analyst':
			return (
				<Badge variant="secondary" className="text-xs">
					Analyst
				</Badge>
			);
	}
}

function statusBadge(status: UserStatus) {
	if (status === 'Active') {
		return (
			<Badge className="border border-green-200 bg-green-50 text-green-700 text-xs hover:bg-green-50">
				Active
			</Badge>
		);
	}
	return (
		<Badge variant="secondary" className="text-xs">
			Inactive
		</Badge>
	);
}

function healthBadge(health: ModuleHealth) {
	switch (health) {
		case 'Healthy':
			return (
				<Badge className="border border-green-200 bg-green-50 text-green-700 text-xs hover:bg-green-50">
					Healthy
				</Badge>
			);
		case 'Warning':
			return (
				<Badge className="border border-yellow-200 bg-yellow-50 text-yellow-700 text-xs hover:bg-yellow-50">
					Warning
				</Badge>
			);
		case 'Error':
			return (
				<Badge className="border border-red-200 bg-red-50 text-red-700 text-xs hover:bg-red-50">
					Error
				</Badge>
			);
	}
}

export default function AdminPage() {
	const [selectedUser, setSelectedUser] = useState<AmlAdminUser | null>(null);
	const [sheetOpen, setSheetOpen] = useState(false);

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<h1 className="text-xl font-semibold text-[#0A0A0A]">Admin</h1>
				<p className="mt-0.5 text-sm text-[#737373]">
					Manage AML users, schedules, and monitor system health
				</p>
			</div>

			<div className="p-6 flex flex-col gap-6">
				<div className="grid grid-cols-4 gap-4">
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Total Users</p>
									<p className="mt-1 text-3xl font-bold text-[#0a0a0a]">24</p>
									<p className="text-xs text-[#737373]">assigned to AML</p>
								</div>
								<Users className="h-5 w-5 text-blue-400" />
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Active Schedules</p>
									<p className="mt-1 text-3xl font-bold text-[#0a0a0a]">8</p>
								</div>
								<Calendar className="h-5 w-5 text-green-400" />
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Pending Assignments</p>
									<p className="mt-1 text-3xl font-bold text-orange-600">12</p>
								</div>
								<Clock className="h-5 w-5 text-orange-400" />
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Overdue Alerts</p>
									<p className="mt-1 text-3xl font-bold text-red-600">5</p>
								</div>
								<AlertOctagon className="h-5 w-5 text-red-400" />
							</div>
						</CardContent>
					</Card>
				</div>

				<Tabs defaultValue="users">
					<TabsList>
						<TabsTrigger value="users">User Management</TabsTrigger>
						<TabsTrigger value="health">System Health</TabsTrigger>
					</TabsList>

					<TabsContent value="users" className="mt-4">
						<div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-white">
							<Table>
								<TableHeader>
									<TableRow className="bg-[#fafafa]">
										<TableHead className="text-xs font-semibold text-[#737373]">
											Name
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Role
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											Active Cases
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											Active Alerts
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Last Login
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Status
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]" />
									</TableRow>
								</TableHeader>
								<TableBody>
									{MOCK_USERS.map((user) => (
										<TableRow key={user.id}>
											<TableCell className="text-sm font-medium text-[#0a0a0a]">
												{user.name}
											</TableCell>
											<TableCell>{roleBadge(user.role)}</TableCell>
											<TableCell className="text-sm text-[#525252] text-right">
												{user.activeCases}
											</TableCell>
											<TableCell className="text-sm text-[#525252] text-right">
												{user.activeAlerts}
											</TableCell>
											<TableCell className="text-sm text-[#525252]">
												{user.lastLogin}
											</TableCell>
											<TableCell>{statusBadge(user.status)}</TableCell>
											<TableCell>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => {
														setSelectedUser(user);
														setSheetOpen(true);
													}}
												>
													View
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</TabsContent>

					<TabsContent value="health" className="mt-4">
						<div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-white">
							<Table>
								<TableHeader>
									<TableRow className="bg-[#fafafa]">
										<TableHead className="text-xs font-semibold text-[#737373]">
											Module
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Status
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Last Run
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											Records Processed
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373] text-right">
											Error Count
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{SYSTEM_MODULES.map((mod) => (
										<TableRow key={mod.id}>
											<TableCell className="text-sm font-medium text-[#0a0a0a]">
												<div className="flex items-center gap-2">
													{mod.status === 'Healthy' ? (
														<CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
													) : mod.status === 'Warning' ? (
														<Clock className="h-4 w-4 text-yellow-500 shrink-0" />
													) : (
														<XCircle className="h-4 w-4 text-red-500 shrink-0" />
													)}
													{mod.module}
												</div>
											</TableCell>
											<TableCell>{healthBadge(mod.status)}</TableCell>
											<TableCell className="text-sm text-[#525252]">
												{mod.lastRun}
											</TableCell>
											<TableCell className="text-sm text-[#525252] text-right">
												{mod.recordsProcessed.toLocaleString()}
											</TableCell>
											<TableCell className="text-right">
												{mod.errorCount > 0 ? (
													<Badge className="border border-red-200 bg-red-50 text-red-700 text-xs hover:bg-red-50">
														{mod.errorCount}
													</Badge>
												) : (
													<span className="text-sm text-[#737373]">0</span>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</TabsContent>
				</Tabs>
			</div>

			<Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
				<SheetContent className="w-[420px] sm:max-w-[420px] overflow-y-auto">
					<SheetHeader>
						<SheetTitle>User Details</SheetTitle>
					</SheetHeader>
					{selectedUser && (
						<div className="mt-4 flex flex-col gap-4">
							<div className="flex items-center gap-3">
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f5f5f5] text-lg font-semibold text-[#0a0a0a]">
									{selectedUser.name.charAt(0)}
								</div>
								<div>
									<p className="font-semibold text-[#0a0a0a]">
										{selectedUser.name}
									</p>
									<p className="text-sm text-[#737373]">{selectedUser.email}</p>
								</div>
							</div>
							<div className="overflow-hidden rounded-lg border border-[#e5e5e5]">
								<Table>
									<TableBody>
										{[
											['Role', roleBadge(selectedUser.role)],
											['Status', statusBadge(selectedUser.status)],
											['Active Cases', String(selectedUser.activeCases)],
											['Active Alerts', String(selectedUser.activeAlerts)],
											['Last Login', selectedUser.lastLogin],
											['Assigned Since', selectedUser.assignedSince],
										].map(([label, value]) => (
											<TableRow key={label as string}>
												<TableCell className="text-xs font-medium text-[#737373] w-36">
													{label}
												</TableCell>
												<TableCell className="text-sm text-[#0a0a0a]">
													{typeof value === 'string' ? value : value}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</div>
					)}
				</SheetContent>
			</Sheet>
		</div>
	);
}
